// One freshness rule for every weather and sea card.
//
// The backend attaches `_freshness` to each payload (or each part of one):
//   { fetched_at, data_time, age_seconds, max_age, stale, source, status: 'ok'|'stale'|'unavailable' }
// Older servers send only { stale, age_seconds, max_age, cached_at }, and some blocks send nothing
// at all but carry their own time ("updated": "15:10", "timestamp", a SAIH "fecha"). Every page
// used to turn these into its own "Hace 3h" pill, and several never looked at them, which is how
// "UV 4 ahora" stayed on screen three hours after the reading. Everything goes through here now.
//
// Times are Europe/Madrid: the data is about Torrelavega, and a phone set to another zone must
// not move "hoy" or "hace 5 min".

export const TZ = 'Europe/Madrid';

const partsFmt = new Intl.DateTimeFormat('en-GB', {
  timeZone: TZ, hourCycle: 'h23',
  year: 'numeric', month: '2-digit', day: '2-digit', hour: '2-digit', minute: '2-digit', second: '2-digit',
});

/** Madrid wall-clock parts of an instant: { y, mo, d, h, mi, s } (month 1-12). */
export function madridParts(ms = Date.now()) {
  const p = {};
  for (const { type, value } of partsFmt.formatToParts(new Date(ms))) p[type] = value;
  return { y: +p.year, mo: +p.month, d: +p.day, h: +p.hour % 24, mi: +p.minute, s: +p.second };
}

/** 'YYYY-MM-DD' in Madrid for an instant (default now). */
export function madridDate(ms = Date.now()) {
  const { y, mo, d } = madridParts(ms);
  return `${y}-${String(mo).padStart(2, '0')}-${String(d).padStart(2, '0')}`;
}

/** Madrid hour 0-23 for an instant (default now). */
export function madridHour(ms = Date.now()) {
  return madridParts(ms).h;
}

/** 'YYYY-MM-DD' shifted by whole days, calendar arithmetic (no DST surprises). */
export function addDays(dateStr, n) {
  const [y, m, d] = dateStr.split('-').map(Number);
  const t = new Date(Date.UTC(y, m - 1, d + n));
  return t.toISOString().slice(0, 10);
}

/** Epoch ms of a Madrid wall-clock time. Corrects for the zone offset at that moment (DST-safe). */
export function madridWallToMs(y, mo, d, h = 0, mi = 0, s = 0) {
  const wall = Date.UTC(y, mo - 1, d, h, mi, s);
  let guess = wall;
  // Two passes settle the offset either side of a DST change.
  for (let i = 0; i < 2; i++) {
    const p = madridParts(guess);
    const asUtc = Date.UTC(p.y, p.mo - 1, p.d, p.h, p.mi, p.s);
    guess = wall - (asUtc - guess);
  }
  return guess;
}

/**
 * Epoch ms for the timestamp shapes the API actually sends, or null.
 *  - ISO with an offset or Z ('2026-09-25T18:10:00+02:00', '…+0000' — Safari rejects the colonless
 *    form, so it is normalised here)
 *  - ISO or 'YYYY-MM-DD HH:MM[:SS]' without an offset: a Madrid wall-clock time (SAIH's `fecha`)
 *  - 'HH:MM': a Madrid clock time with no date (the old `updated` field). Read as its latest
 *    occurrence at or before `now`, i.e. today, or yesterday if that time has not come yet.
 */
export function parseTimestamp(value, now = Date.now()) {
  if (value == null || value === '') return null;
  if (typeof value === 'number') return Number.isFinite(value) ? value : null;
  if (typeof value !== 'string') return null;
  const v = value.trim();

  const clock = /^(\d{1,2}):(\d{2})$/.exec(v);
  if (clock) {
    const h = +clock[1], mi = +clock[2];
    if (h > 23 || mi > 59) return null;
    const today = madridParts(now);
    let t = madridWallToMs(today.y, today.mo, today.d, h, mi);
    if (t > now + 60000) {
      const [y, mo, d] = addDays(madridDate(now), -1).split('-').map(Number);
      t = madridWallToMs(y, mo, d, h, mi);
    }
    return t;
  }

  const m = /^(\d{4})-(\d{2})-(\d{2})(?:[T ](\d{2}):(\d{2})(?::(\d{2})(?:\.\d+)?)?)?\s*(Z|[+-]\d{2}:?\d{2})?$/.exec(v);
  if (!m) return null;
  const [, y, mo, d, h = '0', mi = '0', s = '0', off] = m;
  if (!off) return madridWallToMs(+y, +mo, +d, +h, +mi, +s);
  const utc = Date.UTC(+y, +mo - 1, +d, +h, +mi, +s);
  if (off === 'Z') return utc;
  const sign = off[0] === '-' ? -1 : 1;
  const digits = off.slice(1).replace(':', '');
  return utc - sign * (+digits.slice(0, 2) * 60 + +digits.slice(2, 4)) * 60000;
}

/** The newest parseable time among several (e.g. the stations of one river feed), or null. */
export function latestTime(values, now = Date.now()) {
  let best = null;
  for (const v of values || []) {
    const t = parseTimestamp(v, now);
    if (t != null && (best == null || t > best)) best = t;
  }
  return best;
}

/** «hace 5 min», «hace 3 h», «hace 2 días»; null when the age is unknown. */
export function ageLabel(ageS) {
  if (ageS == null || !Number.isFinite(ageS)) return null;
  const a = Math.max(0, ageS);
  if (a < 60) return 'hace menos de 1 min';
  if (a < 3600) return `hace ${Math.floor(a / 60)} min`;
  if (a < 86400) return `hace ${Math.max(1, Math.round(a / 3600))} h`;
  const days = Math.max(1, Math.round(a / 86400));
  return `hace ${days} ${days === 1 ? 'día' : 'días'}`;
}

export const NO_RECENT_DATA = 'sin datos recientes';

/**
 * The one freshness reading.
 *
 * @param f  a `_freshness` object (new or old shape), or null
 * @param opts.now         epoch ms (tests)
 * @param opts.receivedAt  when the response arrived; the server's age_seconds is carried forward
 *                         from then, so a page left open keeps ageing instead of freezing
 * @param opts.maxAgeS     a stricter limit for this card (UV «ahora»: 45 min, rivers «En vivo»:
 *                         30 min). With a limit and no known age the answer is 'unknown', never ok.
 * @param opts.fallbackTime a time the block carries itself, used when there is no `_freshness`
 *                          (old server). Anything parseTimestamp reads.
 * @param opts.fallbackMaxAgeS the limit to judge that fallback time by when no `maxAgeS` is given.
 * @returns {{ status: 'ok'|'stale'|'unavailable'|'unknown', ageS: number|null, dataTime: number|null,
 *             fresh: boolean, label: string|null, source: string|null }}
 */
export function freshnessOf(f, opts = {}) {
  const { now = Date.now(), receivedAt = null, maxAgeS = null, fallbackTime = null, fallbackMaxAgeS = null } = opts;
  let fr = f && typeof f === 'object' ? f : null;
  let limit = maxAgeS;
  if (!fr && fallbackTime != null) {
    const t = parseTimestamp(fallbackTime, now);
    if (t != null) {
      fr = { data_time: t };
      if (limit == null) limit = fallbackMaxAgeS;
    }
  }
  if (!fr) return { status: 'unknown', ageS: null, dataTime: null, fresh: false, label: null, source: null };

  const dataTime = parseTimestamp(fr.data_time, now) ?? parseTimestamp(fr.fetched_at, now) ?? parseTimestamp(fr.cached_at, now);
  let ageS = null;
  if (typeof fr.age_seconds === 'number' && Number.isFinite(fr.age_seconds)) {
    const carried = receivedAt != null ? Math.max(0, (now - receivedAt) / 1000) : 0;
    ageS = fr.age_seconds + carried;
  } else if (dataTime != null) {
    ageS = Math.max(0, (now - dataTime) / 1000);
  }

  let status;
  if (fr.status === 'unavailable') status = 'unavailable';
  else if (fr.status === 'stale' || fr.stale === true) status = 'stale';
  else if (typeof fr.max_age === 'number' && ageS != null && ageS > fr.max_age) status = 'stale';
  else if (fr.status === 'ok' || fr.stale === false || ageS != null) status = 'ok';
  else status = 'unknown';

  if (limit != null && status === 'ok') {
    if (ageS == null) status = 'unknown';
    else if (ageS > limit) status = 'stale';
  }

  const label = status === 'unavailable' ? NO_RECENT_DATA : (ageLabel(ageS) ?? (status === 'stale' ? NO_RECENT_DATA : null));
  return { status, ageS, dataTime, fresh: status === 'ok', label, source: fr.source || null };
}

const hhmmFmt = new Intl.DateTimeFormat('es-ES', { timeZone: TZ, hour: '2-digit', minute: '2-digit', hourCycle: 'h23' });
const dayFmt = new Intl.DateTimeFormat('es-ES', { timeZone: TZ, day: 'numeric', month: 'short' });

/** 'HH:MM' in Madrid, or '' for an unknown time. */
export function hhmm(ms) {
  return ms == null || !Number.isFinite(ms) ? '' : hhmmFmt.format(new Date(ms));
}

/** 'HH:MM' when the instant is today in Madrid, otherwise '7 sept, HH:MM' (so an old reading
 *  never passes for this afternoon's). */
export function dayTime(ms, now = Date.now()) {
  if (ms == null || !Number.isFinite(ms)) return '';
  if (madridDate(ms) === madridDate(now)) return hhmm(ms);
  return `${dayFmt.format(new Date(ms))}, ${hhmm(ms)}`;
}
